import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';
import { useVoteRoomPrecheckConfirmParams } from './useVoteRoomPrecheckConfirmParams';
import {
  confirmShareholderPrecheckEmail,
  type ShareholderPrecheckConfirmPageResult,
} from '../../services/ShareholderApiClient';
import { SUPPORT_EMAIL, MAILTO_SUPPORT_HREF } from '../../constants/publicSite';

type PageState =
  | { kind: 'loading' }
  | { kind: 'missing-params' }
  | { kind: 'error'; message: string }
  | { kind: 'result'; result: ShareholderPrecheckConfirmPageResult };

type IllustrationVariant = 'loading' | 'success' | 'error';

function ConfirmIllustration({ variant }: { variant: IllustrationVariant }) {
  return (
    <div className="relative mx-auto mt-10 h-44 w-44" aria-hidden>
      <div className="absolute inset-0 rounded-full bg-gray-100/90" />
      <div className="absolute inset-5 rounded-full bg-gray-50" />
      <div className="relative flex h-full w-full items-center justify-center">
        {variant === 'loading' ? (
          <Loader2 size={52} className="text-emerald-600 animate-spin" strokeWidth={1.5} />
        ) : (
          <>
            <Mail size={72} className="text-sky-300" strokeWidth={1.25} />
            <span
              className={`absolute -right-1 top-8 flex h-10 w-10 items-center justify-center rounded-full border-4 border-white shadow-sm ${
                variant === 'success' ? 'bg-emerald-500' : 'bg-red-500'
              }`}
            >
              {variant === 'success' ? (
                <CheckCircle2 size={20} className="text-white" strokeWidth={2.5} />
              ) : (
                <AlertCircle size={20} className="text-white" strokeWidth={2.5} />
              )}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function VoteRoomPrecheckConfirmPage() {
  const params = useVoteRoomPrecheckConfirmParams();
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  useEffect(() => {
    if (!params) {
      setState({ kind: 'missing-params' });
      return;
    }

    let cancelled = false;
    setState({ kind: 'loading' });

    confirmShareholderPrecheckEmail(params)
      .then((result) => {
        if (!cancelled) setState({ kind: 'result', result });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Unable to confirm your email address.',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [params]);

  const copy = useMemo(() => {
    if (state.kind === 'loading') {
      return {
        headline: 'Confirming Your Email',
        message: 'Please wait while we confirm your email address.',
        variant: 'loading' as IllustrationVariant,
      };
    }
    if (state.kind === 'missing-params') {
      return {
        headline: 'Invalid or Expired Verification Link',
        message:
          'This link is missing required information. Please contact your company representative or legal counsel to request a new verification email.',
        variant: 'error' as IllustrationVariant,
      };
    }
    if (state.kind === 'error') {
      return {
        headline: 'Unable to Confirm',
        message: state.message,
        variant: 'error' as IllustrationVariant,
      };
    }
    return {
      headline: state.result.title,
      message: state.result.message,
      variant: (state.result.isError ? 'error' : 'success') as IllustrationVariant,
      companyName: state.result.companyName,
      shareholderName: state.result.shareholderName,
    };
  }, [state]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg text-center">
        <div className="flex justify-center">
          <span className="landing-logo" aria-label="ParachuteIQ">
            parachute<span className="iq">IQ</span>
          </span>
        </div>

        <h1 className="mt-10 text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">{copy.headline}</h1>

        <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base" role="status">
          {copy.message}
        </p>

        {'companyName' in copy && (copy.companyName || copy.shareholderName) ? (
          <p className="mt-4 text-xs text-gray-500">
            {[copy.companyName, copy.shareholderName].filter(Boolean).join(' · ')}
          </p>
        ) : null}

        <ConfirmIllustration variant={copy.variant} />

        {copy.variant === 'success' ? (
          <p className="mt-8 text-xs leading-relaxed text-gray-400">
            This step only confirms we can reach you at this email address. It does not ask you to approve documents or
            cast a vote.
          </p>
        ) : null}

        <p className="mt-10 text-xs text-gray-400">
          Questions?{' '}
          <a href={MAILTO_SUPPORT_HREF} className="text-emerald-700 hover:text-emerald-800 font-medium">
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}
